<script setup lang="ts">
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartConfiguration,
  LinearScale,
  Tooltip,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, Tooltip)

/**
 * One measure per day as thin bars, for the partner statistics. A single
 * series, so the heading above names it and there is no legend; the exact
 * value of each day is in the tooltip.
 */
const props = defineProps<{
  /** ISO dates, one per bar. */
  dates: string[]
  values: number[]
  /** Formats a value for the axis and the tooltip. */
  format?: (value: number) => string
  /** Accessible summary of what the chart shows. */
  label: string
}>()

const canvas = ref<HTMLCanvasElement | null>(null)
const colorMode = useColorMode()
let chart: ChartJS<'bar'> | null = null

const format = (value: number) => props.format ? props.format(value) : value.toLocaleString()
const dayLabel = (iso: string, options: Intl.DateTimeFormatOptions) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', ...options })

function render() {
  if (!canvas.value) return
  chart?.destroy()

  const dark = colorMode.value === 'dark'
  const ink = dark ? '#9CA3AF' : '#747474'
  const grid = dark ? 'rgba(255,255,255,0.08)' : '#EAEDF0'

  const config: ChartConfiguration<'bar'> = {
    type: 'bar',
    data: {
      labels: props.dates.map(date => dayLabel(date, { month: 'short', day: 'numeric' })),
      datasets: [{
        data: props.values,
        backgroundColor: '#0C9347',
        hoverBackgroundColor: '#0A8540',
        borderRadius: { topLeft: 4, topRight: 4 },
        borderSkipped: 'bottom',
        maxBarThickness: 18,
        // 2px surface gap between neighbouring bars.
        categoryPercentage: 0.9,
        barPercentage: 0.9,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      // The whole column is the hover target, not just the bar.
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            title: items => dayLabel(props.dates[items[0].dataIndex], { weekday: 'short', month: 'long', day: 'numeric' }),
            label: item => format(item.parsed.y),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { color: grid },
          ticks: { color: ink, maxRotation: 0, autoSkipPadding: 12, font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: grid },
          border: { display: false },
          ticks: { color: ink, precision: 0, maxTicksLimit: 5, font: { size: 11 }, callback: value => format(Number(value)) },
        },
      },
    },
  }
  chart = new ChartJS(canvas.value, config)
}

onMounted(render)
watch(() => [props.dates, props.values, colorMode.value], render)
onBeforeUnmount(() => chart?.destroy())
</script>

<template>
  <div class="relative h-56 w-full">
    <canvas ref="canvas" role="img" :aria-label="label" />
  </div>
</template>
